import disposeLayerAfterRender from 'utils/layerLifecycle';
import { Feature, Map } from 'ol';
import { Vector as VectorSource } from 'ol/source';
import {
  useCallback,
  useContext, useEffect,
  useState,
} from 'react';
import { Point } from 'ol/geom';
import { fromLonLat } from 'ol/proj';
import {
  Fill, Style, Text,
} from 'ol/style';
import { Vector } from 'ol/layer';
import { createLogger } from 'services/debugLogger';
import BaseLayerContext from '../../BaseLayerContext';
import TimeSeriesContext from '../../TimeSeriesContext';
import { useLazyGetHistoricalRangeIOPointsQuery, useLazyGetIOPointQuery } from '../../../services/metconsole';
import { IOPointRoute } from '../../../types/metconsole.types';
import MainAppContext from '../../MainAppContext';
import { findClosestIsoDate, utcDayjs } from '../../../utils';
import { availableProjections } from '../../../configs/projections';
import { maxZoom, minZoom } from '../../../configs/initialValues';

const logger = createLogger('useConnectedLayers');

function useConnectedLayers(map: Map) {
  const { selectedBaseLayers } = useContext(BaseLayerContext);
  const { currentPosition, preloadingState, range } = useContext(TimeSeriesContext);
  const { playbackMode, realTimeMode, currentProjection } = useContext(MainAppContext);
  const [getIOPoint] = useLazyGetIOPointQuery();
  const [getHistoricalRangeIOPoints] = useLazyGetHistoricalRangeIOPointsQuery();
  const [currentValues, setCurrentValues] = useState<Record<string, string[]>>({});
  const [preloadedData, setPreloadedData] = useState<Record<string, Record<string, string[]>>>();

  function getFontSize(zoom: number, minZoom = 10, maxZoom = 14.5) {
    if (zoom <= 10) return 0;
    if (zoom <= 11.6) return 5;
    if (zoom < 12 && zoom > 11.6) return 10;
    if (zoom >= maxZoom) return 20;
    if (zoom >= 12) {
    // Interpolación lineal de 12 (size=12) a maxZoom (size=20)
      const startZoom = 12;
      const startSize = 12;
      const endZoom = maxZoom;
      const endSize = 30;
      const ratio = (zoom - startZoom) / (endZoom - startZoom);
      return startSize + ratio * (endSize - startSize);
    }
    return 12;
  }

  const styleFunction = useCallback((currentValue: string) => {
    const zoom = map.getView().getZoom() || 10;
    const size = Math.round(getFontSize(zoom, minZoom, maxZoom));
    return new Style({
      text: new Text({
        text: currentValue?.toString(),
        font: `${size}px Roboto`,
        fill: new Fill({
          color: '#000000',
        }),
        offsetX: 0,
        offsetY: 3,
        rotation: 0,
        textAlign: 'center',
      }),
    });
  }, [map]);

  useEffect(() => {
    const getData = async () => {
      const layers = selectedBaseLayers.filter((l) => l.connected);
      const layerP = layers.map((l) => getIOPoint(l.config?.iopointData as IOPointRoute)
        .then((response) => {
          if (!response.data) {
            return ({ [l.name]: [] });
          }

          let responseData: Record<string, string[]> | null = {};
          try {
            responseData = JSON.parse(response.data as string);
            if (responseData === null) {
              return ({ [l.name]: [] });
            }
          } catch (exception) {
            logger.error(`Can't parse iopoint data as JSON. current response: ${response.data}`);
            return ({ [l.name]: [] });
          }

          const areas = l.config?.areasCoordinates;
          if (!areas) {
            return ({ [l.name]: [] });
          }

          const dataList = areas?.reduce((obj, area) => {
            const isaNameFound = responseData && Object.keys(responseData)
              ?.find((data: string) => area.name === data);

            if (!isaNameFound) {
              return { ...obj, [area.name]: [] };
            }

            return obj;
          }, responseData);

          const viewName = Object.keys(dataList)
            .sort((a: string, b: string) => {
              const indexA = areas?.find((area) => area.name === a)?.order;
              const indexB = areas?.find((area) => area.name === b)?.order;

              if (!indexA || !indexB) {
                return 0;
              }

              return indexA - indexB;
            })
            .reduce((acc: string[], key) => {
              const line: string = dataList[key]
                .reduce((str: string, value: string) => (!str ? `${value}` : `${str}\n${value}`), '');
              acc.push(line);
              return acc;
            }, []);

          return ({ [l.name]: viewName });
        }));
      const data = await Promise.all(layerP).then((response) => Object.assign({}, ...response));
      setCurrentValues(data as Record<string, string[]>);
    };

    // This will perform when realtime mode is running.
    // It triggers a simple iopoint request to get the current values.
    if (selectedBaseLayers.some((layer) => layer.connected) && realTimeMode) {
      getData();
    }
  }, [selectedBaseLayers, currentPosition, getIOPoint, realTimeMode]);

  useEffect(() => {
    const getData = async () => {
      const rangeInSeconds = Math.abs(range[1].diff(range[0], 'seconds'));
      const layers = selectedBaseLayers.filter((l) => l.connected);
      const layerP = layers.map((l) => (
        (rangeInSeconds >= (l.config?.maximunRangeToAskForCompleteAlarmsInSeconds || 3600))
          ? Promise.resolve({ [l.name]: [] })
          : getHistoricalRangeIOPoints({
            route: l.config?.iopointData as IOPointRoute,
            range: range.map((d) => d.toISOString()) as string[],
          })
            .then((response) => {
              const dataResponse = response.data as Record<string, string>;
              const parsedData = {} as Record<string, string[]>;
              if (dataResponse) {
                const areas = l.config?.areasCoordinates;
                if (!areas) {
                  return ({ [l.name]: [] });
                }

                Object.keys(dataResponse).forEach((key) => {
                  let responseData = {} as Record<string, string[]>;
                  try {
                    responseData = JSON.parse(dataResponse[key]);
                    if (responseData === null) {
                      return;
                    }
                  } catch (error) {
                    logger.error('Unexpected iopoint data format');
                    return;
                  }
                  const dataList = areas?.reduce((obj, area) => {
                    const isaNameFound = Object
                      .keys(responseData)?.find((data: string) => area.name === data);

                    if (!isaNameFound) {
                      return { ...obj, [area.name]: [] };
                    }

                    return obj;
                  }, responseData);

                  const viewName = Object.keys(dataList)
                    .sort((a: string, b: string) => {
                      const indexA = areas?.find((area) => area.name === a)?.order;
                      const indexB = areas?.find((area) => area.name === b)?.order;

                      if (!indexA || !indexB) {
                        return 0;
                      }

                      return indexA - indexB;
                    })
                    .reduce((acc: string[], key) => {
                      const line: string = dataList[key]
                        .reduce((str: string, value: string) => (!str ? `${value}` : `${str}\n${value}`), '');
                      acc.push(line);
                      return acc;
                    }, []);

                  parsedData[key] = viewName;
                });
                return ({
                  [l.name]: parsedData,
                });
              }

              return ({ [l.name]: [] });
            })));
      const data = await Promise.all(layerP).then((response) => Object.assign({}, ...response));
      setPreloadedData(data);
    };

    // This will perform when preloading data
    if (preloadingState.some((state) => state !== null) && playbackMode) {
      getData();
    }
  }, [getHistoricalRangeIOPoints, preloadingState, range, playbackMode, selectedBaseLayers]);

  useEffect(() => {
    const projection = availableProjections
      .find((projection) => projection.name === currentProjection)?.epsg as string;
    const layers: Vector<VectorSource>[] = [];
    if (Object.keys(currentValues).length) {
      Object.keys(currentValues).forEach((k) => {
        const features: Feature[] = [];
        const layer = selectedBaseLayers.find((l) => l.name === k);
        const layerConfig = layer?.config;
        if (layerConfig && currentValues[k]?.length) {
          layerConfig.interestPoints.forEach((point, index) => {
            const textStyle = () => styleFunction(currentValues[k][index]);
            const feature = new Feature({
              geometry: new Point(fromLonLat([point.lon, point.lat], projection)),
            });
            feature.setStyle(textStyle);
            features.push(feature);
          });

          const vectorSource = new VectorSource({
            features,
          });

          const vectorLayer = new Vector({
            source: vectorSource,
            minZoom: layer.minZoom,
            zIndex: 99999999999,
          });

          layers.push(vectorLayer);

          map.addLayer(vectorLayer);
        }
      });
    }

    return () => {
      layers.forEach((l) => {
        map.removeLayer(l);
        disposeLayerAfterRender(map, l);
      });
    };
  }, [currentProjection, currentValues,
    currentValues.length, map, selectedBaseLayers, styleFunction]);

  useEffect(() => {
    // This will perform when playback mode is running.
    if (playbackMode && currentPosition && preloadedData) {
      Object.keys(preloadedData).forEach((layerName) => {
        const layerPreloadedData = preloadedData[layerName];
        const findClosestDate = findClosestIsoDate(
          layerPreloadedData,
          currentPosition,
        );
        setCurrentValues((prev) => ({
          ...prev,
          [layerName]: layerPreloadedData[findClosestDate] as string[],
        }));
      });
    }
  }, [currentPosition, playbackMode, preloadedData]);

  useEffect(() => {
    const getData = async () => {
      const datetime = utcDayjs(currentPosition);
      const layers = selectedBaseLayers.filter((l) => l.connected);
      const layerP = layers.map((l) => getHistoricalRangeIOPoints({
        route: l.config?.iopointData as IOPointRoute,
        range: [datetime.clone().subtract(l.config?.fetchInterval || 60 * 24 * 5, 'minutes').toISOString(),
          datetime.clone().add(l.config?.fetchInterval || 60 * 24 * 5, 'minutes').toISOString()] as string[],
      })
        .then((response) => {
          if (response.data) {
            const dataResponse = response.data as Record<string, string>;
            const parsedData = {} as Record<string, string[]>;

            const areas = l.config?.areasCoordinates;
            if (!areas) {
              return ({ [l.name]: [] });
            }

            Object.keys(dataResponse).forEach((key) => {
              let responseData = {} as Record<string, string[]>;
              try {
                responseData = JSON.parse(dataResponse[key]);
                if (responseData === null) {
                  return;
                }
              } catch (error) {
                logger.error('Unexpected iopoint data format');
                return;
              }
              const dataList = areas?.reduce((obj, area) => {
                const isaNameFound = Object
                  .keys(responseData)?.find((data: string) => area.name === data);

                if (!isaNameFound) {
                  return { ...obj, [area.name]: [] };
                }

                return obj;
              }, responseData);

              const viewName = Object.keys(dataList)
                .sort((a: string, b: string) => {
                  const indexA = areas?.find((area) => area.name === a)?.order;
                  const indexB = areas?.find((area) => area.name === b)?.order;

                  if (!indexA || !indexB) {
                    return 0;
                  }

                  return indexA - indexB;
                })
                .reduce((acc: string[], key) => {
                  const line: string = dataList[key]
                    .reduce((str: string, value: string) => (!str ? `${value}` : `${str}\n${value}`), '');
                  acc.push(line);
                  return acc;
                }, []);

              parsedData[key] = viewName;
            });

            return {
              [l.name]: parsedData[findClosestIsoDate(parsedData, currentPosition)],
            };
          }
          return { [l.name]: [] };
        }));
      const data = await Promise.all(layerP).then((response) => Object.assign({}, ...response));
      setCurrentValues(data);
    };
    // It will trigger mostly when some intereaction with the slider bar occurs
    if (!playbackMode && !realTimeMode) {
      getData();
    }
  }, [
    getIOPoint,
    playbackMode,
    selectedBaseLayers,
    currentPosition,
    getHistoricalRangeIOPoints,
    realTimeMode,
  ]);
}

export default useConnectedLayers;
